import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {debounce} from '@baidu/health-utils/lib/function/debounce';

import {getTopicItemlist} from '@/services/pages/channel/channel.client';
import {useBottom} from '@/hooks/useBottom';
import {useSearchParams} from '@/hooks/router';
import {getMyVotes, TopicMyVoteResponse} from '@/services/vote.service';
import SvgIcon from '@/components/common/svgIcon';
import * as Logger from '@/utils/ubcUtils';

import type {CardProps} from './card';
import CardList from './cardList';
import Tabs from './tabs';
import styles from './vote.less';

interface Props {
    data: {
        headerPrehotText: string;
        categories: string;
        moduleId: number;
        voteType: string; // 投票类型：voteType: vote  投票，voteType: praise 点赞
    };
}

const Vote = (props: Props) => {
    const {
        headerPrehotText,
        categories,
        moduleId,
        voteType,
    } = props.data || {};

    const tabs = useMemo(
        () => (typeof categories === 'string' ? categories.split(',') : []),
        [categories]
    );

    const [votes, setVotes] = useState<Array<Omit<CardProps, 'status' | 'index' | 'onVote'>>>([]);
    const [voteInfo, setVoteInfo] = useState<TopicMyVoteResponse>({} as TopicMyVoteResponse);
    const [loadingStatus, setLoadingStatus] = useState<'normal' | 'loading' | 'fail' | 'complete'>('normal');
    const pageNo = useRef(1);
    const currentCategory = useRef(tabs[0]);
    const query = useSearchParams();

    const getVoteInfo = useCallback(
        async () => {
            if (moduleId && query?.channel) {
                const result = await getMyVotes({
                    id: query.id,
                    moduleId,
                    fieldValue: query,
                });

                Logger.view({
                    page: 'vote_index',
                    value: 'page',
                    ext: {
                        // eslint-disable-next-line camelcase
                        sf_ref: query.channel,
                        id: query.id,
                    },
                });

                setVoteInfo(result);
            }
        },
        [moduleId, query]
    );

    useEffect(
        () => {
            getVoteInfo();
        },
        [getVoteInfo]
    );

    const fetchVotesData = useCallback(
        async (nextCategory?: string) => {
            if (!nextCategory && (loadingStatus === 'loading' || loadingStatus === 'complete')) {
                return;
            }
            setLoadingStatus('loading');
            const result: {
                count: number;
                list: Array<Omit<CardProps, 'status' | 'index' | 'onVote'>>;
            } = await getTopicItemlist({
                category: currentCategory.current,
                id: query.id,
                moduleId: moduleId,
                pageSize: 30,
                pageNo: pageNo.current || 1,
                preview: query.preview,
            }) as any;

            const votesSlice = result?.list || [];
            const nextVotes = nextCategory ? votesSlice : [...votes, ...votesSlice];
            pageNo.current++;
            setVotes(nextVotes);
            if (result?.count <= nextVotes.length) {
                setLoadingStatus('complete');
            } else {
                setLoadingStatus('normal');
            }
        },
        [loadingStatus, moduleId, query.id, query.preview, votes]
    );

    useBottom(debounce(500, fetchVotesData), {
        distance: 1000,
    });

    useEffect(
        () => {
            fetchVotesData();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleReloadVotes = useCallback(
        () => {
            setLoadingStatus('normal');
            setVotes([]);
            pageNo.current = 1;
            getVoteInfo();
            fetchVotesData(currentCategory.current);
            Logger.clk({
                page: 'vote_index',
                value: 'refresh',
                ext: {
                    // eslint-disable-next-line camelcase
                    sf_ref: query.channel,
                    id: query.id,
                },
            });
        },
        [fetchVotesData, getVoteInfo, query.channel, query.id]
    );

    const handleTabItemClick = useCallback(
        category => {
            pageNo.current = 1;
            currentCategory.current = category;
            setVotes([]);
            fetchVotesData(category);
            Logger.clk({
                page: 'vote_index',
                value: 'tab_clk',
                ext: {
                    // eslint-disable-next-line camelcase
                    sf_ref: query.channel,
                    id: query.id,
                    type: category,
                },
            });
        },
        [fetchVotesData, query.channel, query.id]
    );

    const renderHeader = useMemo(
        () => {
            return (
                <div
                    className={styles(styles.header, {
                        [styles.noStart]: voteInfo?.activityInfo?.status === 3,
                    })}
                >
                    {
                        [
                            voteInfo?.activityInfo?.status === 1
                                && <div className={styles.weaken}>{headerPrehotText}</div>,
                            voteInfo?.activityInfo?.status === 2 && (
                                <div className={styles.processing}>
                                    <div>
                                        {voteType === 'vote' ? '剩余可投票数：' : '剩余点赞机会：'}
                                        {voteInfo?.availableVoteNum === -1 ? '-' : voteInfo?.availableVoteNum}
                                    </div>
                                    <div className={styles.reload} onClick={handleReloadVotes}>
                                        <SvgIcon name="reload.color" className={styles.reloadIcon} />
                                        <span>刷新</span>
                                    </div>
                                </div>
                            ),
                        ]
                    }
                </div>
            );
        },
        [handleReloadVotes, headerPrehotText, voteInfo?.activityInfo?.status, voteInfo?.availableVoteNum]
    );

    const renderTabs = useMemo(
        () => {
            return (
                <Tabs
                    sticky
                    tabs={tabs}
                    onItemClick={handleTabItemClick}
                />
            );
        },
        [tabs, handleTabItemClick]
    );

    const renderLoading = useMemo(
        () => {
            return (
                <div
                    className={styles({
                        [styles.loading]: true,
                        [styles.block]: votes.length > 0 ? 'block' : 'list',
                    })}
                >
                    <img src="https://med-fe.cdn.bcebos.com/cloud-notes/loading_green.gif" className={styles.image} />
                </div>
            );
        },
        [votes.length]
    );

    return (
        <div className={styles.container}>
            <div className={styles.bg}></div>
            <div className={styles.inner}>
                {renderHeader}
                {categories ? renderTabs : null}

                <div className={styles.list}>
                    {
                        votes.length
                            ? (
                                <CardList
                                    cards={votes}
                                    status={voteInfo?.activityInfo?.status}
                                    moduleId={moduleId}
                                    query={query}
                                    getVoteInfo={getVoteInfo}
                                    voteType={voteType}
                                />
                            )
                            : loadingStatus !== 'loading' && <div className={styles.warning}>暂无结果</div>
                    }
                    {
                        loadingStatus === 'loading' ? renderLoading : null
                    }
                </div>
            </div>
        </div>
    );
};

export default memo(Vote);
